import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { dotvotingApi } from '../../services/dotvotingApi';

type BoardPasswordDialogProps = {
  boardId: string | null;
  authToken?: string;
  sessionReady: boolean;
  onLoadBoard: (boardId: string, authToken?: string) => Promise<void>;
};

const BoardPasswordDialog = ({
  boardId,
  authToken,
  sessionReady,
  onLoadBoard,
}: BoardPasswordDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const lastSessionKeyRef = useRef<string | null>(null);
  const onLoadBoardRef = useRef(onLoadBoard);

  useEffect(() => {
    onLoadBoardRef.current = onLoadBoard;
  }, [onLoadBoard]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPassword('');
    setLocalError(null);
  }, [open]);

  useEffect(() => {
    if (!boardId || !sessionReady) {
      return;
    }

    const sessionKey = `${boardId}:${authToken ?? ''}`;
    if (lastSessionKeyRef.current === sessionKey) {
      return;
    }
    lastSessionKeyRef.current = sessionKey;

    let cancelled = false;

    void (async () => {
      dotvotingApi.setSession(boardId, { authToken });

      try {
        const authStatus = await dotvotingApi.getBoardRequiresAuthentication('topic', boardId);
        if (cancelled) {
          return;
        }

        if (authStatus.requiresAuthentication) {
          setError(null);
          setOpen(true);
          return;
        }

        setOpen(false);
        setError(null);
        await onLoadBoardRef.current(boardId, authToken);
      } catch {
        if (!cancelled) {
          setOpen(false);
          setError(null);
          await onLoadBoardRef.current(boardId, authToken);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken, boardId, sessionReady]);

  const handleSubmit = async () => {
    if (!boardId) {
      return;
    }

    const trimmedPassword = password.trim();
    if (!trimmedPassword) {
      setLocalError('Please provide a password');
      return;
    }

    setLocalError(null);
    setError(null);
    setIsAuthenticating(true);

    try {
      await dotvotingApi.authenticateBoard(boardId, trimmedPassword);
      setOpen(false);
      setError(null);
      await onLoadBoard(boardId, authToken);
    } catch (submitError) {
      const message =
        submitError instanceof Error ? submitError.message : 'Unable to authenticate board';
      if (message.toLowerCase().includes('incorrect password')) {
        setError('Incorrect password, please try again');
      } else {
        setError(message);
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="xs">
      <DialogTitle>Password Protected Board</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {localError ? <Alert severity="error">{localError}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            type="password"
            label="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
              }
            }}
            autoFocus
            disabled={isAuthenticating}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={isAuthenticating}>
          Enter Board
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardPasswordDialog;
